// API 基础配置和工具函数

// 应用模式类型
export type AppMode = 'saas' | 'software';

// 环境配置接口
interface AppConfig {
  apiBaseUrl: string;
  appTitle: string;
  appVersion: string;
  appEnv: string;
  appMode: AppMode;
  maxFileSize: number;
  allowedFileTypes: string;
  enableMock: boolean;
  debug: boolean;
  enableDevtools: boolean;
}

// 获取环境变量配置
const getEnvConfig = (): AppConfig => {
  const env = import.meta.env;

  // API 基础 URL 逻辑（简化版 - 适用于前后端同域部署）：
  // 所有环境都使用相对路径 /api，通过 nginx 代理到后端服务
  // 开发环境通过 vite 代理，生产环境通过 nginx 代理
  const apiBaseUrl = '/api';

  return {
    apiBaseUrl,
    appTitle: env.VITE_APP_TITLE || 'WhaleHire 智能招聘系统',
    appVersion: env.VITE_APP_VERSION || '1.0.0',
    appEnv: env.VITE_APP_ENV || 'development',
    appMode: (env.VITE_APP_MODE?.toLowerCase() || 'software') as AppMode,
    maxFileSize: parseInt(env.VITE_MAX_FILE_SIZE) || 10485760, // 10MB
    allowedFileTypes: env.VITE_ALLOWED_FILE_TYPES || '.pdf,.doc,.docx',
    enableMock: env.VITE_ENABLE_MOCK === 'true',
    debug: env.VITE_DEBUG === 'true',
    enableDevtools: env.VITE_ENABLE_DEVTOOLS === 'true',
  };
};

// 导出配置对象
export const appConfig = getEnvConfig();

// 向后兼容的 API_BASE_URL 导出
export const API_BASE_URL = appConfig.apiBaseUrl;

// 调试信息：仅在明确启用 debug 模式时输出
if (appConfig.debug) {
  console.log('[API Config] 当前配置:', {
    apiBaseUrl: appConfig.apiBaseUrl,
    appEnv: appConfig.appEnv,
    debug: appConfig.debug,
    mode: import.meta.env.MODE,
  });
}

// 环境检查工具函数
export const isDevelopment = () => appConfig.appEnv === 'development';
export const isProduction = () => appConfig.appEnv === 'production';

// 应用模式检查工具函数
export const isSaasMode = () => appConfig.appMode === 'saas';
export const isSoftwareMode = () => appConfig.appMode === 'software';

// 调试日志工具 - 分级别输出
export const debugLog = (...args: unknown[]) => {
  // 只在开发环境 && debug 模式下输出
  if (appConfig.debug && import.meta.env.DEV) {
    console.log('[DEBUG]', ...args);
  }
};

// API 调试日志 - 已禁用以避免刷屏
export const apiDebugLog = (..._args: unknown[]) => {
  // 禁用 API 调试日志以避免刷屏
  // 如需启用，取消下面的注释
  // if (appConfig.debug && import.meta.env.DEV) {
  //   console.log('[API DEBUG]', ..._args);
  // }
};

// 全局进行中的 GET 请求去重，避免同一时刻重复发起相同请求
const pendingGetRequests = new Map<string, Promise<unknown>>();

// API 响应类型
export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
  success: boolean;
}

// 分页响应类型
export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// 错误类型
export class ApiError<T = unknown> extends Error {
  constructor(
    public code: number,
    public message: string,
    public data?: T
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

const extractValidationMessages = (data: unknown): string[] => {
  if (!data || typeof data !== 'object') {
    return [];
  }

  const responseData =
    'data' in data && data.data && typeof data.data === 'object'
      ? data.data
      : data;

  if (
    !responseData ||
    typeof responseData !== 'object' ||
    !('validation_errors' in responseData)
  ) {
    return [];
  }

  const validationErrors = responseData.validation_errors;
  if (!Array.isArray(validationErrors)) {
    return [];
  }

  return validationErrors
    .map((item) => {
      if (!item || typeof item !== 'object') {
        return '';
      }
      return 'message' in item && typeof item.message === 'string'
        ? item.message.trim()
        : '';
    })
    .filter(Boolean);
};

export const getApiErrorMessage = (
  error: unknown,
  fallback = '请求失败，请稍后重试'
): string => {
  if (error instanceof ApiError) {
    const validationMessages = extractValidationMessages(error.data);
    if (validationMessages.length > 0) {
      return validationMessages.join('；');
    }

    if (typeof error.message === 'string' && error.message.trim()) {
      return error.message.trim();
    }
  }

  if (error instanceof Error && error.message.trim()) {
    return error.message.trim();
  }

  return fallback;
};

// 基础请求函数
export async function apiRequest<T = unknown>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`;
  const headers = new Headers(options.headers as HeadersInit | undefined);
  const method = options.method?.toUpperCase() ?? 'GET';

  // 设置默认 Content-Type（除非是 FormData）
  if (
    method !== 'GET' &&
    method !== 'HEAD' &&
    !headers.has('Content-Type') &&
    !(options.body instanceof FormData)
  ) {
    headers.set('Content-Type', 'application/json');
  }

  // 配置请求，确保包含 cookies
  const config: RequestInit = {
    ...options,
    headers,
    credentials: 'include', // 重要：确保 cookies 被包含在请求中
  };

  try {
    apiDebugLog(`🌐 API Request: ${method} ${url}`);
    const response = await fetch(url, config);

    // 处理HTTP错误状态
    if (!response.ok) {
      if (response.status === 401) {
        // 未授权，Cookie已失效
        apiDebugLog('🔐 401 Unauthorized - Cookie已失效');

        // 根据当前路径判断重定向到哪个登录页
        const currentPath = window.location.pathname;
        const isAdminPath = currentPath.startsWith('/admin');

        if (isAdminPath && !currentPath.includes('/admin/login')) {
          // 管理后台路径，重定向到管理后台登录页
          apiDebugLog('🔐 Redirecting to admin login page');
          window.location.href = '/admin/login';
        } else if (!isAdminPath && !currentPath.includes('/login')) {
          // 前台路径，重定向到前台登录页
          apiDebugLog('🔐 Redirecting to frontend login page');
          window.location.href = '/login';
        }
        throw new ApiError(401, '登录已过期，请重新登录');
      }

      const errorData = await response.json().catch(() => ({}));
      throw new ApiError(
        response.status,
        errorData.message || `HTTP Error: ${response.status}`,
        errorData
      );
    }

    const data: ApiResponse<T> = await response.json();
    apiDebugLog(`✅ API Response: ${method} ${url} - ${response.status}`);

    return data.data;
  } catch (error) {
    if (error instanceof ApiError) {
      throw error;
    }

    // 网络错误或其他错误
    console.error(`❌ API Error: ${method} ${url}`, error);
    console.error(`❌ Error details:`, {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      name: error instanceof Error ? error.name : typeof error,
      isNetworkError:
        error instanceof TypeError ||
        (error instanceof Error && error.message.includes('ECONNREFUSED')),
    });
    throw new ApiError(0, '网络请求失败，请检查网络连接', error);
  }
}

// GET 请求
export const apiGet = <T = unknown>(
  endpoint: string,
  params?: Record<string, unknown>
): Promise<T> => {
  let finalEndpoint = endpoint;

  if (params) {
    const queryParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        queryParams.append(key, String(value));
      }
    });

    const queryString = queryParams.toString();
    if (queryString) {
      finalEndpoint += (endpoint.includes('?') ? '&' : '?') + queryString;
    }
  }

  const dedupeKey = finalEndpoint;
  const pending = pendingGetRequests.get(dedupeKey) as Promise<T> | undefined;
  if (pending) {
    return pending;
  }

  const requestPromise = apiRequest<T>(finalEndpoint).finally(() => {
    pendingGetRequests.delete(dedupeKey);
  });
  pendingGetRequests.set(dedupeKey, requestPromise as Promise<unknown>);
  return requestPromise;
};

// POST 请求
export function apiPost<T = unknown>(
  endpoint: string,
  data?: Record<string, unknown>,
  options?: RequestInit
): Promise<T>;
export function apiPost<T = unknown>(
  endpoint: string,
  data: FormData,
  options?: RequestInit
): Promise<T>;
export function apiPost<T = unknown>(
  endpoint: string,
  data?: FormData | Record<string, unknown>,
  options: RequestInit = {}
): Promise<T> {
  const isFormData = data instanceof FormData;
  const headers = isFormData
    ? options.headers
    : {
        'Content-Type': 'application/json',
        ...options.headers,
      };

  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: isFormData ? data : data ? JSON.stringify(data) : undefined,
    ...options,
    headers,
  });
}

// PUT 请求
export const apiPut = <T = unknown>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'PUT',
    body: data ? JSON.stringify(data) : undefined,
  });
};

// PATCH 请求
export const apiPatch = <T = unknown>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'PATCH',
    body: data ? JSON.stringify(data) : undefined,
  });
};

// DELETE 请求
export const apiDelete = <T = unknown>(
  endpoint: string,
  data?: Record<string, unknown>
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'DELETE',
    body: data ? JSON.stringify(data) : undefined,
  });
};

// 文件上传请求
export const apiUpload = <T = unknown>(
  endpoint: string,
  formData: FormData
): Promise<T> => {
  return apiRequest<T>(endpoint, {
    method: 'POST',
    body: formData,
    // 不设置 Content-Type，让浏览器自动设置 multipart/form-data
  });
};
